
import { useState } from "react"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { Check, X, Undo2 } from "lucide-react"
import { cn } from "@/lib/utils"

type DiffLine = {
  type: "added" | "removed" | "unchanged" | "modified"
  oldLineNumber?: number
  newLineNumber?: number
  content: string
  id: string
}

type DiffChunk = {
  lines: DiffLine[]
  canApply: boolean
  applied: boolean
  id: string
}

// Sample diff data - in real app this would come from props
const sampleDiff: DiffChunk[] = [
  {
    id: "chunk-1",
    canApply: true,
    applied: false,
    lines: [
      { type: "unchanged", oldLineNumber: 25, newLineNumber: 25, content: "## 📋 **What We're Building**", id: "1" },
      {
        type: "removed",
        oldLineNumber: 27,
        content: "### **Migration Scripts**: `scripts/migrate-content-to-db.js`",
        id: "2",
      },
      {
        type: "added",
        newLineNumber: 27,
        content: "### 🔄 **Migration Scripts**: `scripts/migrate-content-to-db.js`",
        id: "3",
      },
      {
        type: "unchanged",
        oldLineNumber: 30,
        newLineNumber: 30,
        content: "**Concept**: PostgreSQL documents table populated",
        id: "4",
      },
    ],
  },
  {
    id: "chunk-2",
    canApply: true,
    applied: false,
    lines: [
      { type: "removed", oldLineNumber: 35, content: "## 🔍 **Content Analysis First** (30 min)", id: "5" },
      { type: "added", newLineNumber: 35, content: "## 🔍 **Content Analysis First** (30-45 min)", id: "6" },
      { type: "unchanged", oldLineNumber: 36, newLineNumber: 36, content: "", id: "7" },
      {
        type: "added",
        newLineNumber: 37,
        content: "⚠️ **IMPORTANT**: See `2025-10-18-day-2-workflow-preservation-strategy.md` for workflow safety plan!",
        id: "8",
      },
    ],
  },
  {
    id: "chunk-3",
    canApply: true,
    applied: false,
    lines: [
      { type: "removed", oldLineNumber: 41, content: 'find content/ -name "*.md" | head -20', id: "9" },
      { type: "removed", oldLineNumber: 42, content: "find content/ -type d | head -10", id: "10" },
      { type: "added", newLineNumber: 43, content: 'find content/ -name "*.md" | wc -l', id: "11" },
      { type: "added", newLineNumber: 44, content: "find content/ -type d | head -20", id: "12" },
    ],
  },
]

export function DiffViewer() {
  const [chunks, setChunks] = useState(sampleDiff)

  const handleApply = (chunkId: string) => {
    setChunks((prev) => prev.map((chunk) => (chunk.id === chunkId ? { ...chunk, applied: true } : chunk)))
  }

  const handleReject = (chunkId: string) => {
    setChunks((prev) => prev.filter((chunk) => chunk.id !== chunkId))
  }

  const handleUndo = (chunkId: string) => {
    setChunks((prev) => prev.map((chunk) => (chunk.id === chunkId ? { ...chunk, applied: false } : chunk)))
  }

  return (
    <div className="h-full flex flex-col bg-background">
      <ScrollArea className="flex-1">
        <div className="font-mono text-sm">
          {chunks.map((chunk) => (
            <div key={chunk.id} className="relative group">
              {/* Chunk Actions */}
              <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-muted/50 backdrop-blur-sm border-b border-border">
                <span className="text-xs text-muted-foreground">{chunk.applied ? "Applied" : "Pending changes"}</span>
                <div className="flex items-center gap-1">
                  {!chunk.applied ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1.5 text-xs hover:bg-green-500/10 hover:text-green-600 dark:hover:text-green-400"
                        onClick={() => handleApply(chunk.id)}
                      >
                        <Check className="h-3.5 w-3.5" />
                        Apply
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1.5 text-xs hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
                        onClick={() => handleReject(chunk.id)}
                      >
                        <X className="h-3.5 w-3.5" />
                        Reject
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 gap-1.5 text-xs"
                      onClick={() => handleUndo(chunk.id)}
                    >
                      <Undo2 className="h-3.5 w-3.5" />
                      Undo
                    </Button>
                  )}
                </div>
              </div>

              {/* Diff Lines */}
              <div className={cn("transition-opacity", chunk.applied && "opacity-50")}>
                {chunk.lines.map((line) => (
                  <div
                    key={line.id}
                    className={cn(
                      "flex items-start hover:bg-accent/30 transition-colors",
                      line.type === "added" && "bg-green-500/5",
                      line.type === "removed" && "bg-red-500/5",
                    )}
                  >
                    {/* Line Numbers */}
                    <div className="flex items-start gap-0 select-none">
                      <div className="w-12 px-2 py-1 text-right text-muted-foreground/60 text-xs">
                        {line.oldLineNumber || ""}
                      </div>
                      <div className="w-12 px-2 py-1 text-right text-muted-foreground/60 text-xs border-r border-border">
                        {line.newLineNumber || ""}
                      </div>
                    </div>

                    {/* Diff Indicator */}
                    <div className="w-8 flex items-center justify-center py-1 text-xs font-bold">
                      {line.type === "added" && <span className="text-green-600 dark:text-green-400">+</span>}
                      {line.type === "removed" && <span className="text-red-600 dark:text-red-400">-</span>}
                    </div>

                    {/* Content */}
                    <div
                      className={cn(
                        "flex-1 px-2 py-1 whitespace-pre-wrap break-all",
                        line.type === "added" && "text-green-700 dark:text-green-300",
                        line.type === "removed" && "text-red-700 dark:text-red-300",
                      )}
                    >
                      {line.content}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}
